import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentsIcon from '@mui/icons-material/Payments';
import client from '../../../../API';

const PaymentPreferencesCard = React.memo(({ primaryWallet, onPreferencesChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [preferences, setPreferences] = useState({
    auto_accept_payments: false,
    default_payment_destination: 'balance',
    has_payment_method: false,
  });

  // Fetch current preferences
  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await client.creatorConnect.getPaymentPreferences();
      setPreferences({
        auto_accept_payments: response.auto_accept_payments || false,
        default_payment_destination: response.default_payment_destination || 'balance',
        has_payment_method: response.has_payment_method || false,
        primary_wallet: response.primary_wallet,
      });
    } catch (err) {
      console.error('Failed to fetch payment preferences:', err);
      setError('Failed to load payment preferences');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Update preferences
  const updatePreferences = useCallback(async (newPreferences) => {
    try {
      setSaving(true);
      setError(null);
      const response = await client.creatorConnect.updatePaymentPreferences(newPreferences);
      setPreferences(prev => ({
        ...prev,
        auto_accept_payments: response.auto_accept_payments,
        default_payment_destination: response.default_payment_destination,
        primary_wallet: response.primary_wallet,
      }));
      if (onPreferencesChange) {
        onPreferencesChange(response);
      }
    } catch (err) {
      console.error('Failed to update payment preferences:', err);
      if (err.response?.data?.code === 'NO_PAYMENT_METHOD') {
        setError('Please add a verified payment method before enabling auto-withdraw');
      } else {
        setError(err.response?.data?.error || 'Failed to update preferences');
      }
      // Revert the change
      fetchPreferences();
    } finally {
      setSaving(false);
    }
  }, [onPreferencesChange, fetchPreferences]);

  const handleAutoAcceptChange = useCallback((event) => {
    const newValue = event.target.checked;
    setPreferences(prev => ({ ...prev, auto_accept_payments: newValue }));
    updatePreferences({
      auto_accept_payments: newValue,
      default_payment_destination: preferences.default_payment_destination,
    });
  }, [preferences.default_payment_destination, updatePreferences]);

  const handleDestinationChange = useCallback((event) => {
    const newValue = event.target.value;
    setPreferences(prev => ({ ...prev, default_payment_destination: newValue }));
    updatePreferences({
      auto_accept_payments: preferences.auto_accept_payments,
      default_payment_destination: newValue,
    });
  }, [preferences.auto_accept_payments, updatePreferences]);

  const walletDisplayName = preferences.primary_wallet?.display_name ||
    primaryWallet?.nickname ||
    primaryWallet?.wallet_type?.replace('_', ' ').toUpperCase() ||
    'Primary Payment Method';

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        mb: 3,
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AutorenewIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Auto-Accept Payment Settings
          </Typography>
          <Tooltip title="When enabled, approved invoices will be automatically processed without requiring you to manually claim each payout.">
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={preferences.auto_accept_payments}
                onChange={handleAutoAcceptChange}
                disabled={saving}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Auto-accept payments
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Automatically process payouts when business users approve your invoices
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', m: 0 }}
          />
        </Box>

        <Collapse in={preferences.auto_accept_payments}>
          <Box
            sx={{
              pl: 2,
              borderLeft: 2,
              borderColor: 'primary.light',
              ml: 1,
              mt: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Default payment destination
            </Typography>
            <FormControl component="fieldset" disabled={saving}>
              <RadioGroup
                value={preferences.default_payment_destination}
                onChange={handleDestinationChange}
              >
                <FormControlLabel
                  value="balance"
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceWalletIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Add to Blitz Balance
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Funds are credited to your Blitz balance for later withdrawal
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1.5 }}
                />
                <FormControlLabel
                  value="withdraw"
                  control={<Radio size="small" />}
                  disabled={!preferences.has_payment_method && !primaryWallet}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaymentsIcon fontSize="small" color="action" />
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: (!preferences.has_payment_method && !primaryWallet) ? 'text.disabled' : 'inherit'
                          }}
                        >
                          Withdraw to {walletDisplayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(!preferences.has_payment_method && !primaryWallet)
                            ? 'Add a payment method to enable this option'
                            : 'Funds are automatically sent to your primary payment method'}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Collapse>

        {!preferences.auto_accept_payments && (
          <Alert severity="info" sx={{ mt: 2 }}>
            With auto-accept disabled, you will need to manually claim each payout from the Money page.
          </Alert>
        )}

        {saving && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

export default PaymentPreferencesCard;
